import {
  selectPendingRecruitmentActions,
  selectPlayers,
  selectUnits,
} from "../redux/reducers/sharedStateReducer/selectors";
import {getActingPlayerFromState} from "../utils/players/getActingPlayer";
import {getPlayer0xColor} from "../utils/players/getPlayer0xColor";
import {RECRUITMENT_PULSE_COUNT, resolveUnitRecruitmentCast} from "../configs/unitRecruitmentAnimation";
import {createLandingCoinsSpentAnimation, playCoinsSpentAnimation} from "./playCoinsSpentAnimation";
import {runAbilityCast} from "./playAbilityCastEffects";
import {buildUnitRecruitmentCastPlan, buildUnitSizeTransferCastPlan} from "../utils/effects/buildAbilityCast";
import {captureUnitSizeTransferHold} from "../utils/effects/abilityResultHolds";

// Новобранцы за снабжение, вставшие в этот промежуток, звучат один раз
const SUPPLY_RECRUITS_SOUND_GAP_MS = 150;
let lastSupplyRecruitSoundAt = 0;

/**
 * Эффекты найма отряда: отряд проявляется на казарме из света (см.
 * UNIT_RECRUITMENT_CAST: чем круче тип, тем круче свет). В момент проявления
 * настоящий отряд встает на поле, звучит найм, расходится волна цветом нанявшего
 * игрока и над отрядом всплывает число потраченных монет.
 *
 * Проявление случается РОВНО ОДИН РАЗ в любом случае: нет поля, скрытая вкладка,
 * перемотка записи, быстрые боты — тогда отряд встает сразу, и звук с волной не
 * теряются (см. runAbilityCast).
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {string} params.unitId — нанятый отряд
 * @param {string} params.hexKey — ячейка казармы, где идет найм
 * @param {number} [params.price] — сколько монет стоил найм: столько всплывает
 *   над отрядом (см. playCoinsSpentAnimation)
 * @param {boolean} [params.isPaced] — найм чужой (бот, соперник по сети,
 *   запись матча)
 * @param {Function} [params.playRecruitmentSound] — звук найма: зовется в момент
 *   проявления отряда, а не в момент действия
 * @param {Function} [params.playCastSound] — подъем под разгон старших типов (только с анимацией)
 * @param {Function} [params.playImpactSound] — удар на проявлении старших типов (только с анимацией)
 */
export const playUnitRecruitmentEffects = (store, hexGridRenderer, {
  unitId, hexKey, price, isPaced, playRecruitmentSound, playCastSound, playImpactSound,
} = {}) => {
  // Число монет всплывет на проявлении — если трата к тому времени еще свежа
  // (анимация, начатая в скрытой вкладке, доходит до него уже по возвращении)
  const showCoinsSpent = createLandingCoinsSpentAnimation(store, hexGridRenderer, {hexKey, amount: price, isPaced});
  const unit = findRecruitedUnit(store, unitId, hexKey);
  let isCastShown = false;
  const land = () => {
    hexGridRenderer?.revealUnitSprites?.(unitId);
    // Найм могли отменить, пока отряд проявлялся: тогда ни звука, ни волны, ни
    // числа монет
    if (!findRecruitedUnit(store, unitId, hexKey)) {
      return;
    }

    playRecruitmentSound?.();
    if (isCastShown) {
      playImpactSound?.();
    }
    playRecruitmentWave(store, hexGridRenderer, unitId, hexKey);
    showCoinsSpent();
  };

  if (!hexGridRenderer || !unit) {
    land();
    return;
  }

  runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: unit.ownerId,
    buildPlan: () => {
      const anchor = hexGridRenderer.getAbilityCastAnchor(hexKey);
      if (!anchor) {
        return null;
      }
      const state = store.getState();
      const players = selectPlayers(state);
      const art = hexGridRenderer.getUnitRecruitmentArt?.(unit, {players, state});
      const owner = (players || []).find((player) => player.id === unit.ownerId);
      hexGridRenderer.hideRecruitingUnit?.(unit);
      return buildUnitRecruitmentCastPlan({
        unit: anchor,
        hexSize: anchor.hexSize,
        unitType: unit.type,
        color: art?.tint ?? (owner ? getPlayer0xColor(owner.color) : 0xFFFFFF),
        art,
        hexRotation: art?.hexRotation,
      });
    },
    onCast: () => {
      isCastShown = true;
      playCastSound?.();
    },
    onResult: land,
  });
};

/**
 * Новобранец крысолюдов за снабжение проявляется на ферме той же анимацией, что и отряд
 * на казарме. Монет он не стоит, поэтому числа над ним нет. Звук один на отряды,
 * вставшие разом: авто-раздача и итог фазы ставят их пачкой, и он сложился бы в удар.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} soundManager
 * @param {Object} params
 * @param {Object} params.unit — отряд, который стал виден
 * @param {boolean} [params.isPaced] — найм чужой (сеть, запись матча)
 */
export const playSupplyRecruitEffects = (store, hexGridRenderer, soundManager, {unit, isPaced} = {}) => {
  playUnitRecruitmentEffects(store, hexGridRenderer, {
    unitId: unit.id,
    hexKey: unit.hexKey,
    price: 0,
    isPaced,
    playRecruitmentSound: () => {
      const now = Date.now();
      if (now - lastSupplyRecruitSoundAt < SUPPLY_RECRUITS_SOUND_GAP_MS) {
        return;
      }
      lastSupplyRecruitSoundAt = now;
      soundManager?.playUnitSelect?.(unit.type, unit.ownerId, getActingPlayerFromState(store.getState()), unit);
    },
  });
};

const findRecruitedUnit = (store, unitId, hexKey) => (selectUnits(store.getState()) || [])
  .find((unit) => unit.id === unitId && unit.hexKey === hexKey);

/**
 * Докупка размера отряду: от квартала к отряду летит золотой шар (см.
 * buildUnitSizeTransferCastPlan), а когда долетит, у отряда появляется «+N», на
 * ячейке, где он СТОИТ, расходится одинарная подсветка и над ним всплывает число
 * потраченных монет. Появления отряда здесь нет: новый жетон на поле не появляется.
 *
 * Без анимации (скрытая вкладка, перемотка, быстрые боты, нет квартала) все это
 * случается сразу
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {string} params.unitId — отряд, которому докупили размер
 * @param {string} [params.quarterHexKey] — квартал, с которого докуплен размер
 * @param {number} [params.amount] — сколько размера докуплено
 * @param {number} [params.price] — сколько монет стоила докупка
 * @param {boolean} [params.isPaced] — докупка чужая (бот, соперник по сети,
 *   запись матча)
 * @param {Function} [params.playTransferSound] — волшебный звук полета (только с анимацией)
 */
export const playUnitSizeRecruitmentEffects = (store, hexGridRenderer, {
  unitId, quarterHexKey, amount, price, isPaced, playTransferSound,
} = {}) => {
  const unit = (selectUnits(store.getState()) || []).find((item) => item.id === unitId);
  if (!hexGridRenderer || !unit) {
    return;
  }

  const showArrival = () => {
    // Докупку могли отменить, пока шар летел: тогда ни подсветки, ни числа монет
    const isCancelled = quarterHexKey && !(selectPendingRecruitmentActions(store.getState()) || [])
      .some((action) => action.quarterHexKey === quarterHexKey);
    if (isCancelled) {
      return;
    }
    playRecruitmentPulse(store, hexGridRenderer, unitId, unit.hexKey, {count: RECRUITMENT_PULSE_COUNT.SIZE});
    playCoinsSpentAnimation(store, hexGridRenderer, {hexKey: unit.hexKey, amount: price, isPaced});
  };

  if (!quarterHexKey) {
    showArrival();
    return;
  }
  runAbilityCast(store, hexGridRenderer, {
    isPaced,
    actorOwnerId: unit.ownerId,
    buildPlan: () => {
      const quarter = hexGridRenderer.getAbilityCastAnchor(quarterHexKey);
      const unitAnchor = hexGridRenderer.getAbilityCastAnchor(unit.hexKey);
      return quarter && unitAnchor
        ? buildUnitSizeTransferCastPlan({quarter, unit: unitAnchor, hexSize: unitAnchor.hexSize, amount})
        : null;
    },
    hold: captureUnitSizeTransferHold({quarterHexKey}),
    onCast: playTransferSound,
    onResult: showArrival,
  });
};

/** Волна цветом игрока на найме: чем круче тип, тем шире и дольше */
const playRecruitmentWave = (store, hexGridRenderer, unitId, hexKey) => {
  const unit = findRecruitedUnit(store, unitId, hexKey);
  const {waveCount, waveEndFactor, waveDurationMs} = resolveUnitRecruitmentCast(unit?.type);
  playRecruitmentPulse(store, hexGridRenderer, unitId, hexKey, {
    count: waveCount,
    endScaleFactor: waveEndFactor,
    durationMs: waveDurationMs,
  });
};

/** Волна по ячейке цветом игрока, который нанял отряд */
const playRecruitmentPulse = (store, hexGridRenderer, unitId, hexKey, options) => {
  if (typeof hexGridRenderer?.showUnitRecruitmentPulse !== 'function') {
    return;
  }

  const state = store.getState();
  const unit = findRecruitedUnit(store, unitId, hexKey);
  const owner = (selectPlayers(state) || []).find((player) => player.id === unit?.ownerId);
  if (!owner) {
    return;
  }

  try {
    hexGridRenderer.showUnitRecruitmentPulse(hexKey, getPlayer0xColor(owner.color), state, options);
  } catch (error) {
    console.warn('[Recruitment] не удалось показать волну найма', error);
  }
};
